import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, formatRp, API } from "@/lib/api";
import { useSettings, assetUrl } from "@/lib/settings";
import SellerProducts from "@/pages/SellerProducts";
import SellerWallet from "@/pages/SellerWallet";
import WithdrawalManager from "@/pages/WithdrawalManager";
import {
  Warehouse, Truck, Package, AlertTriangle, Wallet, Building2, ArrowRightLeft, MessageSquare,
  CheckCircle2, Route, Store, Sparkles, UserCog, Upload, CreditCard, Settings as SettingsIcon,
  XCircle, ExternalLink, Palette, Package2, Ship
} from "lucide-react";

const StatCard = ({ label, value, tone = "cyan", sub, testid }) => (
  <Card data-testid={testid} className="glass glow-border bg-transparent rounded-xl p-5">
    <div className="text-[10px] font-mono-metric uppercase tracking-widest text-cyan-400">{label}</div>
    <div className={`mt-2 font-mono-metric text-2xl font-bold ${tone === "green" ? "text-emerald-300" : tone === "red" ? "text-red-300" : tone === "amber" ? "text-amber-300" : "text-white"}`}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </Card>
);

// Role → allowed tab keys
const ROLE_TABS = {
  admin: ["warehouse","route","inventory","finance","entities","sellers","sales","employees","payments","shipping","cms","withdrawals"],
  warehouse: ["warehouse","inventory","route"],
  driver: ["route"],
  finance: ["finance","entities","payments","sales","shipping","withdrawals"],
  seller: ["seller-products","seller-wallet"],
};

export default function Dashboard() {
  const { refresh: refreshSettings } = useSettings();
  const user = JSON.parse(localStorage.getItem("ti_user") || "{}");
  const role = user.role || "admin";
  const allowed = ROLE_TABS[role] || ["warehouse"];

  const [orders, setOrders] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [inv, setInv] = useState({ products: [], critical: [] });
  const [supplierLog, setSupplierLog] = useState([]);
  const [fin, setFin] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [entities, setEntities] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [sales, setSales] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [payments, setPayments] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [rejectSeller, setRejectSeller] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const [newEmp, setNewEmp] = useState({ email:"", password:"", name:"", role:"warehouse" });
  const [shipQuote, setShipQuote] = useState({ open:false, orderId:null, dest:"", weight:1, options:[] });

  const load = async () => {
    const calls = [];
    if (allowed.includes("warehouse") || allowed.includes("route")) calls.push(api.get("/orders").then(r=>setOrders(r.data)));
    if (allowed.includes("route")) calls.push(api.get("/delivery/routes").then(r=>setRoutes(r.data.routes)));
    if (allowed.includes("inventory") || allowed.includes("warehouse")) {
      calls.push(api.get("/inventory").then(r=>setInv(r.data)));
      calls.push(api.get("/inventory/supplier-log").then(r=>setSupplierLog(r.data)));
    }
    if (allowed.includes("finance")) {
      calls.push(api.get("/finance/summary").then(r=>setFin(r.data)));
      calls.push(api.get("/finance/invoices").then(r=>setInvoices(r.data)));
    }
    if (allowed.includes("entities")) calls.push(api.get("/finance/entities").then(r=>setEntities(r.data)));
    if (allowed.includes("sellers")) calls.push(api.get("/sellers").then(r=>setSellers(r.data)));
    if (allowed.includes("sales")) calls.push(api.get("/sales").then(r=>setSales(r.data)));
    if (allowed.includes("employees")) calls.push(api.get("/employees").then(r=>setEmployees(r.data)));
    if (allowed.includes("payments")) calls.push(api.get("/payments").then(r=>setPayments(r.data)));
    if (allowed.includes("shipping")) calls.push(api.get("/shipments").then(r=>setShipments(r.data)));
    if (allowed.includes("cms")) calls.push(api.get("/settings").then(r=>setSiteSettings(r.data)));
    await Promise.all(calls);
  };
  useEffect(() => { load().catch(e => toast.error("Gagal load data")); }, []);

  const triggerRestock = async (product_id) => {
    try { const r = await api.post("/inventory/trigger-restock", { product_id }); toast.success(`WA supplier terkirim: ${r.data.quantity} unit`); load(); }
    catch { toast.error("Gagal"); }
  };
  const switchEntity = async (id) => {
    try { await api.post("/finance/entity/switch", { entity_id: id }); toast.success("Entitas CV aktif diubah"); load(); }
    catch { toast.error("Gagal"); }
  };
  const payInvoice = async (no) => {
    try { await api.post(`/finance/invoices/${encodeURIComponent(no)}/pay`); toast.success("Invoice ditandai lunas"); load(); }
    catch { toast.error("Gagal"); }
  };
  const approveSeller = async (email) => {
    try {
      const r = await api.post(`/sellers/${encodeURIComponent(email)}/approve`, { note: "OK" });
      if (r.data.login_password) {
        toast.success(`Approved. Password login: ${r.data.login_password}`, { duration: 15000 });
        alert(`Seller disetujui!\n\nEmail login: ${r.data.login_email}\nPassword: ${r.data.login_password}\n\nBagikan ke seller via WhatsApp / email. Password ini tidak dapat dilihat lagi.`);
      } else {
        toast.success("Seller disetujui");
      }
      load();
    } catch { toast.error("Gagal"); }
  };
  const doReject = async () => {
    try { await api.post(`/sellers/${encodeURIComponent(rejectSeller.email)}/reject`, { note: rejectNote }); toast.success("Seller ditolak"); setRejectSeller(null); setRejectNote(""); load(); }
    catch { toast.error("Gagal"); }
  };
  const addEmployee = async () => {
    if (!newEmp.email || !newEmp.password || !newEmp.name) { toast.error("Lengkapi data"); return; }
    try { await api.post("/employees", newEmp); toast.success("Karyawan ditambah"); setNewEmp({ email:"", password:"", name:"", role:"warehouse" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const toggleEmp = async (email) => {
    try { await api.post(`/employees/${encodeURIComponent(email)}/toggle`); load(); }
    catch { toast.error("Gagal"); }
  };
  const createPayment = async (order_id) => {
    try { const r = await api.post("/payment/duitku/create", { order_id, method:"duitku" });
      toast.success(`VA: ${r.data.va_number}`);
      load();
    } catch { toast.error("Gagal buat payment"); }
  };
  const callbackPayment = async (ref, status) => {
    try { await api.post("/payment/duitku/callback", { duitku_ref: ref, status });
      toast.success(`Callback ${status}`); load();
    } catch { toast.error("Gagal"); }
  };
  const cekOngkir = async () => {
    if (!shipQuote.dest) { toast.error("Isi tujuan"); return; }
    try {
      const r = await api.post("/shipping/cost", { origin:"Karawang", destination: shipQuote.dest, weight_kg: parseFloat(shipQuote.weight) || 1 });
      setShipQuote(q => ({ ...q, options: r.data.options }));
    } catch { toast.error("Gagal cek ongkir"); }
  };
  const bookShip = async (opt) => {
    try {
      await api.post("/shipping/book", { order_id: shipQuote.orderId, courier: opt.courier, service: opt.service, cost: opt.cost });
      toast.success(`Booked ${opt.courier} ${opt.service}`);
      setShipQuote({ open:false, orderId:null, dest:"", weight:1, options:[] });
      load();
    } catch { toast.error("Gagal book"); }
  };

  // CMS
  const [uploading, setUploading] = useState({});
  const updateSetting = (key, val) => setSiteSettings(s => ({ ...s, [key]: val }));
  const saveSettings = async () => {
    try { await api.put("/settings", { settings: siteSettings }); toast.success("Pengaturan disimpan"); refreshSettings(); }
    catch { toast.error("Gagal simpan"); }
  };
  const uploadImg = async (key, file) => {
    if (!file) return;
    setUploading(u => ({ ...u, [key]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/upload`, { method:"POST", body: fd });
      const d = await r.json();
      updateSetting(key, d.url);
      toast.success(`${key} diperbarui`);
    } catch { toast.error("Upload gagal"); }
    setUploading(u => ({ ...u, [key]: false }));
  };

  return (
    <div className="tech-grid min-h-[calc(100vh-4rem)] py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="pill-cyan mb-2">ADMIN CONTROL · {role.toUpperCase()}</div>
            <h1 className="font-display text-3xl font-bold text-white">Operational Dashboard</h1>
            <p className="text-slate-400 text-sm">Halo, <span className="text-cyan-300">{user.name || "Admin"}</span></p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard testid="stat-orders-total" label="Total Order" value={orders.length}/>
          <StatCard testid="stat-orders-confirmed" label="Confirmed" tone="green" value={orders.filter(o=>o.status==="confirmed"||o.status==="shipped").length}/>
          <StatCard testid="stat-inv-critical" label="Stok Kritis" tone="red" value={inv.critical.length}/>
          <StatCard testid="stat-revenue" label="Revenue" value={fin ? formatRp(fin.revenue_confirmed) : "-"}/>
        </div>

        <Tabs defaultValue={allowed[0]} className="w-full">
          <div className="overflow-x-auto -mx-4 px-4 mb-4">
            <TabsList className="bg-slate-900/60 border border-slate-800 inline-flex">
              {allowed.includes("warehouse") && <TabsTrigger value="warehouse" data-testid="tab-warehouse"><Warehouse className="w-4 h-4 mr-1.5"/>Gudang</TabsTrigger>}
              {allowed.includes("route") && <TabsTrigger value="route" data-testid="tab-route"><Route className="w-4 h-4 mr-1.5"/>AI Route</TabsTrigger>}
              {allowed.includes("inventory") && <TabsTrigger value="inventory" data-testid="tab-inventory"><Package className="w-4 h-4 mr-1.5"/>Inventory</TabsTrigger>}
              {allowed.includes("finance") && <TabsTrigger value="finance" data-testid="tab-finance"><Wallet className="w-4 h-4 mr-1.5"/>Finance</TabsTrigger>}
              {allowed.includes("entities") && <TabsTrigger value="entities" data-testid="tab-entities"><Building2 className="w-4 h-4 mr-1.5"/>Multi-CV</TabsTrigger>}
              {allowed.includes("sellers") && <TabsTrigger value="sellers" data-testid="tab-sellers"><Store className="w-4 h-4 mr-1.5"/>Sellers</TabsTrigger>}
              {allowed.includes("sales") && <TabsTrigger value="sales" data-testid="tab-sales"><Sparkles className="w-4 h-4 mr-1.5"/>Sales</TabsTrigger>}
              {allowed.includes("employees") && <TabsTrigger value="employees" data-testid="tab-employees"><UserCog className="w-4 h-4 mr-1.5"/>Karyawan</TabsTrigger>}
              {allowed.includes("payments") && <TabsTrigger value="payments" data-testid="tab-payments"><CreditCard className="w-4 h-4 mr-1.5"/>Duitku</TabsTrigger>}
              {allowed.includes("shipping") && <TabsTrigger value="shipping" data-testid="tab-shipping"><Ship className="w-4 h-4 mr-1.5"/>Ekspedisi</TabsTrigger>}
              {allowed.includes("cms") && <TabsTrigger value="cms" data-testid="tab-cms"><Palette className="w-4 h-4 mr-1.5"/>CMS Website</TabsTrigger>}
              {allowed.includes("seller-products") && <TabsTrigger value="seller-products" data-testid="tab-seller-products"><Store className="w-4 h-4 mr-1.5"/>Produk Saya</TabsTrigger>}
              {allowed.includes("seller-wallet") && <TabsTrigger value="seller-wallet" data-testid="tab-seller-wallet"><Wallet className="w-4 h-4 mr-1.5"/>Wallet & Withdraw</TabsTrigger>}
              {allowed.includes("withdrawals") && <TabsTrigger value="withdrawals" data-testid="tab-withdrawals"><CreditCard className="w-4 h-4 mr-1.5"/>Withdrawal</TabsTrigger>}
            </TabsList>
          </div>

          {/* WAREHOUSE */}
          <TabsContent value="warehouse">
            <Card className="glass glow-border bg-transparent rounded-2xl p-6">
              <h3 className="font-display font-bold text-white text-lg mb-4">Order Queue Gudang</h3>
              <div className="space-y-2">
                {orders.length === 0 && <div className="text-slate-500 text-sm">Belum ada order.</div>}
                {orders.map(o => (
                  <div key={o.order_id} data-testid={`wh-order-${o.order_id}`} className="bg-slate-900/40 border border-slate-800 rounded-lg p-4 flex justify-between items-center flex-wrap gap-2">
                    <div>
                      <div className="font-mono-metric text-cyan-300 text-sm">{o.order_id}</div>
                      <div className="text-white text-sm">{o.partner.name} · {o.partner.sppg_name}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{o.items.length} item · {formatRp(o.total)}</div>
                      {o.referral_code && <div className="text-[10px] text-amber-300 font-mono-metric mt-0.5">REF: {o.referral_code}</div>}
                    </div>
                    <div className={`px-3 py-1 text-xs font-mono-metric rounded-md ${
                      o.status === "confirmed" ? "bg-emerald-500/20 text-emerald-300" :
                      o.status === "shipped" ? "bg-cyan-500/20 text-cyan-300" :
                      "bg-amber-500/20 text-amber-300"
                    }`}>{o.status.toUpperCase()}</div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* ROUTE */}
          <TabsContent value="route">
            <Card className="glass glow-border bg-transparent rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-white text-lg">AI Route Delivery <span className="pill-cyan ml-2">NEAREST-FIRST</span></h3>
                <Truck className="w-6 h-6 text-cyan-400"/>
              </div>
              <div className="space-y-2">
                {routes.length === 0 && <div className="text-slate-500 text-sm">Belum ada rute aktif.</div>}
                {routes.map((r, i) => (
                  <div key={r.order_id} data-testid={`route-${i}`} className="bg-slate-900/40 border border-slate-800 rounded-lg p-4 flex items-center gap-4 flex-wrap">
                    <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-300 font-mono-metric font-bold">{i+1}</div>
                    <div className="flex-1 min-w-[180px]">
                      <div className="text-white text-sm font-semibold">{r.sppg_name}</div>
                      <div className="text-xs text-slate-400">{r.address}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono-metric text-cyan-300 text-lg font-bold">{r.distance_km} km</div>
                      <div className="text-xs text-slate-500">ETA {r.eta_min} min · {r.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* INVENTORY */}
          <TabsContent value="inventory">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card className="glass glow-border bg-transparent rounded-2xl p-6">
                  <h3 className="font-display font-bold text-white text-lg mb-4">Stock Min/Max Monitor</h3>
                  <div className="space-y-3">
                    {inv.products.map(p => {
                      const pct = Math.min(100, (p.stock / p.max_stock) * 100);
                      const critical = p.stock <= p.min_stock;
                      return (
                        <div key={p.id} data-testid={`inv-${p.id}`} className="bg-slate-900/40 border border-slate-800 rounded-lg p-4">
                          <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
                            <div>
                              <div className="text-white font-medium text-sm">{p.name}</div>
                              <div className="text-xs text-slate-500 font-mono-metric">Min {p.min_stock} · Max {p.max_stock}</div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className={`font-mono-metric font-bold text-lg ${critical ? "text-red-400" : "text-cyan-300"}`}>{p.stock} {p.unit}</div>
                              {critical && <Button data-testid={`restock-${p.id}`} size="sm" onClick={() => triggerRestock(p.id)} className="bg-amber-500 hover:bg-amber-400 text-slate-950 h-8 text-xs"><MessageSquare className="w-3 h-3 mr-1"/>WA Supplier</Button>}
                            </div>
                          </div>
                          <Progress value={pct} className={`h-1.5 ${critical ? "[&>*]:bg-red-500" : "[&>*]:bg-cyan-400"}`}/>
                          {critical && <div className="text-xs text-red-400 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3"/>Stok kritis — auto-alert aktif</div>}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </div>
              <div>
                <Card className="glass glow-border bg-transparent rounded-2xl p-6">
                  <h3 className="font-display font-bold text-white text-lg mb-4">Log Supplier WA</h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {supplierLog.length === 0 && <div className="text-slate-500 text-sm">Belum ada trigger.</div>}
                    {supplierLog.map((l, i) => (
                      <div key={i} className="bg-slate-900/40 border border-slate-800 rounded-lg p-3 text-xs">
                        <div className="text-cyan-300 font-mono-metric">{l.product_name} · {l.quantity_requested} unit</div>
                        <div className="text-slate-400 mt-1">{l.message}</div>
                        <div className="text-slate-600 font-mono-metric text-[10px] mt-1">{new Date(l.sent_at).toLocaleString("id-ID")}</div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* FINANCE */}
          <TabsContent value="finance">
            <div className="grid lg:grid-cols-4 gap-4 mb-4">
              <StatCard testid="fin-revenue" label="Revenue" value={fin ? formatRp(fin.revenue_confirmed) : "-"} tone="green"/>
              <StatCard testid="fin-unpaid" label="Piutang" value={fin ? formatRp(fin.unpaid) : "-"} tone="red"/>
              <StatCard testid="fin-paid" label="Terbayar" value={fin ? formatRp(fin.paid) : "-"} tone="green"/>
              <StatCard testid="fin-tax" label="Kewajiban Pajak" value={fin ? formatRp(fin.tax_liability) : "-"} tone="amber"/>
            </div>
            <Card className="glass glow-border bg-transparent rounded-2xl p-6">
              <h3 className="font-display font-bold text-white text-lg mb-4">Invoice · Agentic Finance AI</h3>
              <div className="space-y-2">
                {invoices.length === 0 && <div className="text-slate-500 text-sm">Belum ada invoice.</div>}
                {invoices.map(iv => (
                  <div key={iv.invoice_no} data-testid={`inv-${iv.invoice_no}`} className="bg-slate-900/40 border border-slate-800 rounded-lg p-4 flex justify-between items-center flex-wrap gap-2">
                    <div>
                      <div className="font-mono-metric text-cyan-300 text-sm">{iv.invoice_no}</div>
                      <div className="text-white text-sm">{iv.entity}</div>
                      <div className="text-xs text-slate-500 font-mono-metric">NPWP {iv.npwp} · Order {iv.order_id}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono-metric text-white font-bold">{formatRp(iv.amount)}</div>
                      <div className="mt-1 flex items-center gap-2 justify-end">
                        <span className={`text-xs px-2 py-0.5 rounded font-mono-metric ${iv.status === "paid" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{iv.status.toUpperCase()}</span>
                        {iv.status === "unpaid" && <Button data-testid={`pay-${iv.invoice_no}`} size="sm" onClick={() => payInvoice(iv.invoice_no)} className="h-7 bg-cyan-400 hover:bg-cyan-300 text-slate-950 text-xs font-bold"><CheckCircle2 className="w-3 h-3 mr-1"/>Rekonsiliasi</Button>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* ENTITIES */}
          <TabsContent value="entities">
            <Card className="glass glow-border bg-transparent rounded-2xl p-6">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h3 className="font-display font-bold text-white text-lg">Multi-Entity CV · Tax Planning</h3>
                <div className="pill-cyan">SUBJECT TO APPLICABLE TAX REGULATIONS</div>
              </div>
              <p className="text-sm text-slate-400 mb-6">Sistem auto-switch entitas CV saat omzet mendekati threshold.</p>
              <div className="grid md:grid-cols-3 gap-4">
                {entities.map(e => {
                  const pct = Math.min(100, (e.revenue_ytd / e.threshold) * 100);
                  return (
                    <div key={e.id} data-testid={`entity-${e.id}`} className={`rounded-xl p-5 border-2 ${e.active ? "border-cyan-400 bg-cyan-500/5 shadow-[0_0_24px_rgba(0,240,255,0.2)]" : "border-slate-800 bg-slate-900/40"}`}>
                      <div className="flex items-center justify-between mb-3">
                        <Building2 className={`w-6 h-6 ${e.active ? "text-cyan-400" : "text-slate-500"}`}/>
                        {e.active && <span className="pill-cyan">ACTIVE</span>}
                      </div>
                      <div className="text-white font-semibold text-sm">{e.name}</div>
                      <div className="text-xs text-slate-500 font-mono-metric">NPWP {e.npwp}</div>
                      <div className="mt-4">
                        <div className="text-[10px] font-mono-metric text-cyan-400 uppercase tracking-widest">OMZET YTD</div>
                        <div className="font-mono-metric text-lg text-white font-bold">{formatRp(e.revenue_ytd)}</div>
                        <div className="text-xs text-slate-500 font-mono-metric">/ {formatRp(e.threshold)}</div>
                        <Progress value={pct} className="mt-2 h-1.5 [&>*]:bg-cyan-400"/>
                      </div>
                      <div className="mt-3 text-xs text-slate-400">Tarif Pajak Final: <span className="text-emerald-300 font-mono-metric">{(e.tax_rate*100).toFixed(2)}%</span></div>
                      {!e.active && <Button data-testid={`switch-${e.id}`} onClick={() => switchEntity(e.id)} size="sm" variant="outline" className="w-full mt-3 border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10 text-xs"><ArrowRightLeft className="w-3 h-3 mr-1"/>Aktifkan CV Ini</Button>}
                    </div>
                  );
                })}
              </div>
            </Card>
          </TabsContent>

          {/* SELLERS */}
          <TabsContent value="sellers">
            <Card className="glass glow-border bg-transparent rounded-2xl p-6">
              <h3 className="font-display font-bold text-white text-lg mb-4">Marketplace Seller Approval</h3>
              <div className="space-y-2">
                {sellers.length === 0 && <div className="text-slate-500 text-sm">Belum ada seller mendaftar.</div>}
                {sellers.map(s => (
                  <div key={s.email} data-testid={`seller-${s.email}`} className="bg-slate-900/40 border border-slate-800 rounded-lg p-4 flex justify-between items-center flex-wrap gap-3">
                    <div className="flex-1 min-w-[220px]">
                      <div className="text-white font-semibold">{s.shop_name}</div>
                      <div className="text-xs text-slate-400">{s.name} · {s.email} · {s.phone}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{s.city} · {s.category}</div>
                      <div className="flex gap-2 mt-2">
                        {s.ktp_url && <a href={assetUrl(s.ktp_url)} target="_blank" rel="noreferrer" className="text-[10px] font-mono-metric text-cyan-300 hover:underline flex items-center gap-1"><ExternalLink className="w-3 h-3"/>KTP</a>}
                        {s.nib_url && <a href={assetUrl(s.nib_url)} target="_blank" rel="noreferrer" className="text-[10px] font-mono-metric text-cyan-300 hover:underline flex items-center gap-1"><ExternalLink className="w-3 h-3"/>NIB</a>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded font-mono-metric ${
                        s.status === "approved" ? "bg-emerald-500/20 text-emerald-300" :
                        s.status === "rejected" ? "bg-red-500/20 text-red-300" :
                        "bg-amber-500/20 text-amber-300"
                      }`}>{s.status.toUpperCase()}</span>
                      {s.status === "pending_review" && (
                        <>
                          <Button data-testid={`approve-${s.email}`} size="sm" onClick={() => approveSeller(s.email)} className="h-8 bg-emerald-400 hover:bg-emerald-300 text-slate-950 font-bold text-xs"><CheckCircle2 className="w-3 h-3 mr-1"/>Setujui</Button>
                          <Button data-testid={`reject-${s.email}`} size="sm" variant="outline" onClick={() => setRejectSeller(s)} className="h-8 border-red-500/40 text-red-300 hover:bg-red-500/10 text-xs"><XCircle className="w-3 h-3 mr-1"/>Tolak</Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Dialog open={!!rejectSeller} onOpenChange={(o) => !o && setRejectSeller(null)}>
              <DialogContent className="bg-slate-900 border-slate-700 text-white">
                <DialogHeader><DialogTitle>Tolak Seller: {rejectSeller?.shop_name}</DialogTitle></DialogHeader>
                <Textarea data-testid="reject-note" value={rejectNote} onChange={e=>setRejectNote(e.target.value)} placeholder="Alasan penolakan…" className="bg-slate-800 border-slate-700"/>
                <Button data-testid="reject-confirm" onClick={doReject} className="bg-red-500 hover:bg-red-400 text-white">Kirim Penolakan</Button>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* SALES FREELANCE */}
          <TabsContent value="sales">
            <Card className="glass glow-border bg-transparent rounded-2xl p-6">
              <h3 className="font-display font-bold text-white text-lg mb-4">Sales Freelance & Komisi 0,5%</h3>
              <div className="space-y-2">
                {sales.length === 0 && <div className="text-slate-500 text-sm">Belum ada sales.</div>}
                {sales.map(s => (
                  <div key={s.phone} data-testid={`sales-${s.phone}`} className="bg-slate-900/40 border border-slate-800 rounded-lg p-4 flex justify-between items-center flex-wrap gap-3">
                    <div className="flex-1 min-w-[200px]">
                      <div className="text-white font-semibold">{s.name}</div>
                      <div className="text-xs text-slate-400">{s.phone} · {s.email}</div>
                      <div className="text-xs text-slate-500">{s.bank_name} · {s.bank_account}</div>
                    </div>
                    <div className="pill-cyan">{s.referral_code}</div>
                    <div className="text-right">
                      <div className="text-[10px] font-mono-metric text-cyan-400">TOTAL TRX</div>
                      <div className="font-mono-metric text-white font-bold">{formatRp(s.total_transactions)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] font-mono-metric text-amber-400">KOMISI</div>
                      <div className="font-mono-metric text-amber-300 font-bold">{formatRp(s.total_commission)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* EMPLOYEES */}
          <TabsContent value="employees">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card className="glass glow-border bg-transparent rounded-2xl p-6">
                  <h3 className="font-display font-bold text-white text-lg mb-4">Daftar Karyawan</h3>
                  <div className="space-y-2">
                    {employees.map(e => (
                      <div key={e.email} data-testid={`emp-${e.email}`} className="bg-slate-900/40 border border-slate-800 rounded-lg p-3 flex justify-between items-center flex-wrap gap-2">
                        <div>
                          <div className="text-white font-medium text-sm">{e.name}</div>
                          <div className="text-xs text-slate-400 font-mono-metric">{e.email}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="pill-cyan uppercase">{e.role}</span>
                          <Button data-testid={`toggle-${e.email}`} size="sm" variant="outline" onClick={() => toggleEmp(e.email)} className={`h-7 text-xs ${e.active ? "border-red-500/40 text-red-300 hover:bg-red-500/10" : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"}`}>{e.active ? "Nonaktifkan" : "Aktifkan"}</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
              <div>
                <Card className="glass glow-border bg-transparent rounded-2xl p-6">
                  <h3 className="font-display font-bold text-white text-lg mb-4">Tambah Karyawan</h3>
                  <div className="space-y-3">
                    <Input data-testid="emp-name" placeholder="Nama" value={newEmp.name} onChange={e=>setNewEmp({...newEmp,name:e.target.value})} className="bg-slate-900/60 border-slate-700 text-white"/>
                    <Input data-testid="emp-email" placeholder="Email" value={newEmp.email} onChange={e=>setNewEmp({...newEmp,email:e.target.value})} className="bg-slate-900/60 border-slate-700 text-white"/>
                    <Input data-testid="emp-password" type="password" placeholder="Password" value={newEmp.password} onChange={e=>setNewEmp({...newEmp,password:e.target.value})} className="bg-slate-900/60 border-slate-700 text-white"/>
                    <Select value={newEmp.role} onValueChange={v=>setNewEmp({...newEmp,role:v})}>
                      <SelectTrigger data-testid="emp-role" className="bg-slate-900/60 border-slate-700 text-white"><SelectValue/></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700 text-white">
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="warehouse">Gudang</SelectItem>
                        <SelectItem value="driver">Driver</SelectItem>
                        <SelectItem value="finance">Finance</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button data-testid="emp-add" onClick={addEmployee} className="w-full bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold">Tambah</Button>
                  </div>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* PAYMENTS (DUITKU) */}
          <TabsContent value="payments">
            <Card className="glass glow-border bg-transparent rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className="font-display font-bold text-white text-lg">Duitku Payment Gateway <span className="pill-cyan ml-2">MOCKED</span></h3>
              </div>
              <p className="text-xs text-slate-400 mb-4">Buat transaksi Duitku (Virtual Account) untuk order confirmed. Simulasi callback SUCCESS/FAILED tersedia.</p>
              <div className="mb-4">
                <div className="text-[10px] font-mono-metric text-cyan-400 uppercase tracking-widest mb-2">ORDER SIAP DIBAYAR</div>
                <div className="space-y-2">
                  {orders.filter(o => o.status !== "pending_confirmation").slice(0, 5).map(o => (
                    <div key={o.order_id} className="bg-slate-900/40 border border-slate-800 rounded-lg p-3 flex justify-between items-center">
                      <div>
                        <div className="text-white text-sm">{o.order_id}</div>
                        <div className="text-xs text-slate-400">{formatRp(o.total)}</div>
                      </div>
                      <Button data-testid={`create-pay-${o.order_id}`} size="sm" onClick={() => createPayment(o.order_id)} className="h-8 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold text-xs">Buat Duitku VA</Button>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-mono-metric text-cyan-400 uppercase tracking-widest mb-2">RIWAYAT PAYMENT</div>
                <div className="space-y-2">
                  {payments.length === 0 && <div className="text-slate-500 text-sm">Belum ada payment.</div>}
                  {payments.map(p => (
                    <div key={p.duitku_ref} data-testid={`pay-${p.duitku_ref}`} className="bg-slate-900/40 border border-slate-800 rounded-lg p-3 flex justify-between items-center flex-wrap gap-2">
                      <div>
                        <div className="font-mono-metric text-cyan-300 text-sm">{p.duitku_ref}</div>
                        <div className="text-xs text-slate-400">Order {p.order_id} · VA {p.va_number}</div>
                        <div className="text-xs text-slate-500 font-mono-metric">{formatRp(p.amount)}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded font-mono-metric ${p.status === "SUCCESS" ? "bg-emerald-500/20 text-emerald-300" : p.status === "FAILED" ? "bg-red-500/20 text-red-300" : "bg-amber-500/20 text-amber-300"}`}>{p.status}</span>
                        {p.status === "PENDING" && <>
                          <Button size="sm" onClick={() => callbackPayment(p.duitku_ref, "SUCCESS")} className="h-7 bg-emerald-400 hover:bg-emerald-300 text-slate-950 text-xs">SUCCESS</Button>
                          <Button size="sm" variant="outline" onClick={() => callbackPayment(p.duitku_ref, "FAILED")} className="h-7 border-red-500/40 text-red-300 text-xs">FAILED</Button>
                        </>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* SHIPPING */}
          <TabsContent value="shipping">
            <Card className="glass glow-border bg-transparent rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className="font-display font-bold text-white text-lg">Ekspedisi Multi-Kurir <span className="pill-cyan ml-2">MOCKED</span></h3>
              </div>
              <p className="text-xs text-slate-400 mb-4">Cek ongkir JNE, J&T, SiCepat, AnterAja, Ninja Xpress dari 1 API.</p>
              <div className="mb-4">
                <div className="text-[10px] font-mono-metric text-cyan-400 uppercase tracking-widest mb-2">ORDER YG PERLU KIRIM</div>
                <div className="space-y-2">
                  {orders.filter(o => o.status === "confirmed").slice(0, 5).map(o => (
                    <div key={o.order_id} className="bg-slate-900/40 border border-slate-800 rounded-lg p-3 flex justify-between items-center flex-wrap gap-2">
                      <div>
                        <div className="text-white text-sm">{o.order_id}</div>
                        <div className="text-xs text-slate-400">{o.partner.sppg_name} · {o.partner.address}</div>
                      </div>
                      <Button data-testid={`quote-${o.order_id}`} size="sm" onClick={() => setShipQuote({ open:true, orderId:o.order_id, dest:o.partner.address, weight:10, options:[] })} className="h-8 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold text-xs">Cek Ongkir</Button>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-mono-metric text-cyan-400 uppercase tracking-widest mb-2">RIWAYAT PENGIRIMAN</div>
                <div className="space-y-2">
                  {shipments.length === 0 && <div className="text-slate-500 text-sm">Belum ada.</div>}
                  {shipments.map(s => (
                    <div key={s.tracking} className="bg-slate-900/40 border border-slate-800 rounded-lg p-3 flex justify-between items-center flex-wrap gap-2">
                      <div>
                        <div className="font-mono-metric text-cyan-300 text-sm">{s.tracking}</div>
                        <div className="text-xs text-slate-400">{s.courier} · {s.service} · Order {s.order_id}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono-metric text-white font-bold">{formatRp(s.cost)}</div>
                        <span className="text-xs px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-300 font-mono-metric">{s.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            <Dialog open={shipQuote.open} onOpenChange={(o) => !o && setShipQuote({ open:false, orderId:null, dest:"", weight:1, options:[] })}>
              <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl">
                <DialogHeader><DialogTitle>Cek Ongkir · Order {shipQuote.orderId}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Kota tujuan" value={shipQuote.dest} onChange={e=>setShipQuote({...shipQuote, dest:e.target.value})} className="bg-slate-800 border-slate-700"/>
                  <Input placeholder="Berat (kg)" type="number" value={shipQuote.weight} onChange={e=>setShipQuote({...shipQuote, weight:e.target.value})} className="bg-slate-800 border-slate-700"/>
                </div>
                <Button data-testid="cek-ongkir-btn" onClick={cekOngkir} className="bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold">Cek Ongkir</Button>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {shipQuote.options.map((opt, i) => (
                    <div key={i} className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 flex justify-between items-center">
                      <div>
                        <div className="text-white text-sm font-semibold">{opt.courier} · {opt.service}</div>
                        <div className="text-xs text-slate-400">ETA {opt.eta_days} hari</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="font-mono-metric text-cyan-300 font-bold">{formatRp(opt.cost)}</div>
                        <Button data-testid={`book-${opt.courier_code}-${opt.service}`} size="sm" onClick={() => bookShip(opt)} className="h-8 bg-emerald-400 hover:bg-emerald-300 text-slate-950 text-xs font-bold">Pilih</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* CMS */}
          <TabsContent value="cms">
            <Card className="glass glow-border bg-transparent rounded-2xl p-6 space-y-6">
              <div>
                <h3 className="font-display font-bold text-white text-lg mb-1">CMS Website Editor</h3>
                <p className="text-xs text-slate-400">Semua tampilan landing page dapat diedit di sini. Klik Simpan setelah perubahan.</p>
              </div>

              {/* Images */}
              <div>
                <div className="text-[10px] font-mono-metric text-cyan-400 uppercase tracking-widest mb-3">GAMBAR</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { key: "logo_url", label: "Logo" },
                    { key: "favicon_url", label: "Favicon" },
                    { key: "wa_mockup_url", label: "Hero WhatsApp Mockup" },
                    { key: "confirm_mockup_url", label: "Konfirmasi Mockup" },
                  ].map(({key,label}) => (
                    <div key={key} className="bg-slate-900/40 border border-slate-800 rounded-lg p-3">
                      <div className="text-xs text-slate-300 mb-2">{label}</div>
                      <div className="h-28 bg-slate-950 rounded-md flex items-center justify-center overflow-hidden mb-2">
                        {siteSettings[key] ? <img src={assetUrl(siteSettings[key])} alt={label} className="max-h-full max-w-full object-contain"/> : <Package2 className="w-8 h-8 text-slate-700"/>}
                      </div>
                      <label className="flex items-center gap-1 text-xs text-cyan-300 hover:text-cyan-200 cursor-pointer">
                        <Upload className="w-3 h-3"/>{uploading[key] ? "Mengunggah..." : "Ganti gambar"}
                        <input data-testid={`upload-${key}`} type="file" accept="image/*" className="hidden" onChange={e => uploadImg(key, e.target.files?.[0])}/>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Text */}
              <div>
                <div className="text-[10px] font-mono-metric text-cyan-400 uppercase tracking-widest mb-3">TEKS HERO & CTA</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { k:"page_title", l:"Page Title (tab browser)" },
                    { k:"tagline", l:"Tagline" },
                    { k:"hero_title", l:"Hero Title" },
                    { k:"hero_highlight", l:"Hero Highlight (kata biru)" },
                    { k:"hero_subtitle", l:"Hero Subtitle" },
                    { k:"cta_primary", l:"CTA Utama" },
                    { k:"cta_secondary", l:"CTA Sekunder" },
                    { k:"cta_seller", l:"CTA Seller" },
                    { k:"footer_contact", l:"Footer" },
                    { k:"footer_email", l:"Footer Email" },
                    { k:"footer_phone", l:"Footer Phone" },
                    { k:"accent_color", l:"Warna Aksen (hex)" },
                  ].map(({k,l}) => (
                    <div key={k}>
                      <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-metric">{l}</Label>
                      <Input data-testid={`cms-${k}`} value={siteSettings[k] || ""} onChange={e => updateSetting(k, e.target.value)} className="mt-1 bg-slate-900/60 border-slate-700 text-white h-10"/>
                    </div>
                  ))}
                </div>
                <div className="mt-3">
                  <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-metric">Hero Description</Label>
                  <Textarea data-testid="cms-hero_description" value={siteSettings.hero_description || ""} onChange={e => updateSetting("hero_description", e.target.value)} className="mt-1 bg-slate-900/60 border-slate-700 text-white"/>
                </div>
              </div>

              <Button data-testid="cms-save" onClick={saveSettings} className="w-full h-11 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold"><SettingsIcon className="w-4 h-4 mr-2"/>Simpan Semua Perubahan</Button>
            </Card>
          </TabsContent>

          {/* SELLER PRODUCTS */}
          <TabsContent value="seller-products">
            <SellerProducts/>
          </TabsContent>

          {/* SELLER WALLET */}
          <TabsContent value="seller-wallet">
            <SellerWallet/>
          </TabsContent>

          {/* ADMIN/FINANCE WITHDRAWALS */}
          <TabsContent value="withdrawals">
            <WithdrawalManager role={role}/>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
